// Quote Book — time-series quote management with as-of date resolution
import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import { useAsOfDate } from '../../hooks/useAsOfDate';
import { QUOTE_KINDS, QUOTE_TYPES, CURRENCIES, QuoteKind, QuoteType } from '../../lib/types';
import {
  QuoteBookEntry, QuoteResolutionMode,
  getQuoteBook, saveQuoteBookEntry,
  upsertDatedValue, deleteDatedValue,
  resolveQuoteValue, buildQuoteSnapshot,
  getResolutionMode, setResolutionMode as setResolutionModePersist,
  exportQuoteBook, importQuoteBook,
} from '../../lib/storage/quoteBook';
import { getCreditCurves } from '../../lib/storage/creditCurves';
import {
  getMdBackendSettings,
  getMonthOverviewForQuoteBook,
  hydrateMonthSeriesForQuoteBook,
  prefetchNeighborMonthsForQuoteBook,
  resolveAsOfForQuoteBook,
  type MdMonthOverview,
  type MdResolveResult,
} from '../../lib/marketDataBackend';
import PanelPlaceholder from '../../components/ui/PanelPlaceholder';
import { entityUi } from '../../components/ui/entityUi';
import { formStyles } from '../../components/ui/formStyles';

export default function QuoteBook() {
  const quoteUi = entityUi.quote;
  const { asOfDate } = useAsOfDate();
  const [entries, setEntries] = useState<QuoteBookEntry[]>([]);
  const [resolutionMode, setResolutionModeState] = useState<QuoteResolutionMode>('previous');
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [showNewQuote, setShowNewQuote] = useState(false);
  const [showSnapshot, setShowSnapshot] = useState(false);
  const [filterCurrency, setFilterCurrency] = useState('');
  const [filterType, setFilterType] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [searchParams] = useSearchParams();
  const [calendarMonth, setCalendarMonth] = useState<{ year: number; month: number }>(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [hydrating, setHydrating] = useState(false);
  const [monthOverview, setMonthOverview] = useState<MdMonthOverview | null>(null);
  const [resolveStats, setResolveStats] = useState<MdResolveResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEntries(getQuoteBook());
    setResolutionModeState(getResolutionMode());
  }, []);

  useEffect(() => {
    const param = searchParams.get('type');
    if (param === 'Curve' || param === 'Volatility' || param === 'Credit') {
      setFilterType(param);
    }
  }, [searchParams]);

  const creditQuoteIds = useMemo(() => {
    const ids = new Set<string>();
    for (const curve of getCreditCurves()) {
      for (const p of curve.points || []) {
        if (p.quote_id) ids.add(p.quote_id);
      }
    }
    return ids;
  }, [entries.length]);

  const effectiveQuoteType = (entry: QuoteBookEntry): QuoteType => {
    if (entry.quote_type) return entry.quote_type;
    return creditQuoteIds.has(entry.id) ? 'Credit' : 'Curve';
  };

  const getEffectiveResolutionMode = (entry: QuoteBookEntry): QuoteResolutionMode =>
    entry.resolution_mode || resolutionMode;

  const reload = () => setEntries(getQuoteBook());

  const handleResolutionChange = (mode: QuoteResolutionMode) => {
    setResolutionModePersist(mode);
    setResolutionModeState(mode);
  };

  const filtered = entries.filter(e => {
    if (filterCurrency && e.currency !== filterCurrency) return false;
    if (filterType && effectiveQuoteType(e) !== filterType) return false;
    return true;
  });

  const effectiveSelectedDate = selectedDate || asOfDate;
  const snapshot = buildQuoteSnapshot(entries, effectiveSelectedDate, resolutionMode);

  const formatValue = (entry: QuoteBookEntry, val: number) => {
    if (entry.kind === 'Rate' || entry.kind === 'Spread') return `${(val * 100).toFixed(4)}%`;
    if (entry.kind === 'FxSpot' || entry.kind === 'FxPoints') return val.toFixed(4);
    return val.toFixed(2);
  };

  // Export
  const handleExport = () => {
    const data = exportQuoteBook(asOfDate);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quantra_quotes_v1_${asOfDate}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    flash('success', 'Quote book exported');
  };

  // Import
  const handleImport = async (file: File) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const result = importQuoteBook(data, true);
      reload();
      if (result.errors.length > 0) {
        flash('error', `Imported ${result.imported} quotes with errors: ${result.errors.join(', ')}`);
      } else {
        flash('success', `Imported ${result.imported} quotes (replaced)`);
      }
    } catch {
      flash('error', 'Invalid JSON file');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const flash = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const selectedEntry = selectedQuoteId ? entries.find(e => e.id === selectedQuoteId) : null;

  useEffect(() => {
    if (!selectedDate) setSelectedDate(asOfDate);
  }, [asOfDate, selectedDate]);

  useEffect(() => {
    if (!effectiveSelectedDate) return;
    const d = new Date(effectiveSelectedDate);
    if (!isNaN(d.getTime())) {
      setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
    }
  }, [effectiveSelectedDate]);

  const handleDateChange = (value: string) => {
    setSelectedDate(value);
  };

  const handleMonthChange = (next: { year: number; month: number }) => {
    setHydrating(true);
    setCalendarMonth(next);
  };

  const handleFetchSelectedDate = async (silent: boolean = false) => {
    const dateToFetch = effectiveSelectedDate;
    if (!dateToFetch) return;
    const md = getMdBackendSettings();
    if (!md.enabled || !md.baseUrl) {
      if (!silent) flash('error', 'Enable Market Data Backend in Settings first');
      return;
    }
    try {
      const result = await resolveAsOfForQuoteBook(md.baseUrl, dateToFetch);
      setResolveStats(result);
      reload();
      if (!silent && result.foundQuotes > 0) {
        flash('success', `Fetched ${result.foundQuotes} series for ${dateToFetch}`);
      } else {
        if (!silent) flash('error', `No values resolved for ${dateToFetch}`);
      }
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'unknown error';
      if (!silent) flash('error', `Date fetch failed: ${reason}`);
    }
  };

  useEffect(() => {
    let isCancelled = false;
    const md = getMdBackendSettings();
    if (!md.enabled || !md.baseUrl || !effectiveSelectedDate) return;

    const run = async () => {
      setHydrating(true);
      try {
        try {
          const overview = await getMonthOverviewForQuoteBook(md.baseUrl, calendarMonth.year, calendarMonth.month + 1);
          if (!isCancelled) setMonthOverview(overview);
        } catch {
          // Keep previous overview if month overview fetch fails.
        }

        try {
          await hydrateMonthSeriesForQuoteBook(md.baseUrl, calendarMonth.year, calendarMonth.month + 1, 400);
        } catch {
          // Hydration is best-effort; calendar coverage is fetched separately.
        }

        try {
          await prefetchNeighborMonthsForQuoteBook(md.baseUrl, calendarMonth.year, calendarMonth.month + 1, 250);
        } catch {
          // Ignore prefetch failures.
        }

        try {
          await handleFetchSelectedDate(true);
        } catch {
          // Ignore per-date resolve failures in background mode.
        }
      } catch {
        // Keep the UI responsive even if background flow fails unexpectedly.
      } finally {
        if (!isCancelled) setHydrating(false);
      }
    };

    const timer = setTimeout(run, 180);
    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [calendarMonth.year, calendarMonth.month, effectiveSelectedDate, entries.length]);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
        {/* Title row */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-[#0a0a0a]">Quote Book</h1>
            <p className="text-[#737373] mt-1">
              Time-series quotes with as-of date resolution — resolved at <span className="font-mono text-[#8a6a2f] font-medium">{effectiveSelectedDate}</span>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowSnapshot(!showSnapshot)}
              className="px-3 py-2 text-sm font-medium text-[#525252] bg-white border border-[#d4d4d4] rounded-lg hover:bg-[#f5f5f5] transition-colors"
            >
              {showSnapshot ? 'Hide' : 'Show'} Snapshot
            </button>
            <button
              onClick={() => { setShowNewQuote(true); setSelectedQuoteId(null); }}
              className="px-4 py-2 text-sm font-medium text-white bg-[#0a0a0a] rounded-lg hover:bg-[#262626] transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Quote
            </button>
          </div>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        {/* Controls bar */}
        <div className="bg-white border border-[#e5e5e5] rounded-xl p-4 mb-4">
          <div className="flex gap-3 items-center flex-wrap">
            <select value={filterCurrency} onChange={e => setFilterCurrency(e.target.value)}
              className="px-3 py-1.5 text-sm border border-[#d4d4d4] rounded-lg">
              <option value="">All currencies</option>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className="px-3 py-1.5 text-sm border border-[#d4d4d4] rounded-lg">
              <option value="">All quote types</option>
              {QUOTE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>

            <div className="flex items-center gap-2 ml-auto">
              <label className="text-xs text-[#737373] font-medium">Date:</label>
              <input
                type="date"
                value={effectiveSelectedDate}
                onChange={e => handleDateChange(e.target.value)}
                className="px-2 py-1 text-xs border border-[#d4d4d4] rounded-lg focus:outline-none focus:border-[#8a6a2f]"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-[#737373] font-medium">Resolution:</label>
              <select value={resolutionMode} onChange={e => handleResolutionChange(e.target.value as QuoteResolutionMode)}
                className="px-2 py-1 text-xs border border-[#d4d4d4] rounded-lg">
                <option value="previous">Previous (≤ date)</option>
                <option value="exact">Exact match</option>
              </select>
            </div>

            <div className="flex gap-1">
              <button onClick={handleExport}
                className="px-3 py-1.5 text-xs font-medium text-[#525252] bg-white border border-[#d4d4d4] rounded-lg hover:bg-[#f5f5f5]"
                title="Export quote book JSON">
                Export
              </button>
              <input ref={fileInputRef} type="file" accept=".json" className="hidden" id="qb-import"
                onChange={e => e.target.files?.[0] && handleImport(e.target.files[0])} />
              <label htmlFor="qb-import"
                className="px-3 py-1.5 text-xs font-medium text-[#525252] bg-white border border-[#d4d4d4] rounded-lg hover:bg-[#f5f5f5] cursor-pointer">
                Import
              </label>
            </div>

            <span className="text-xs text-[#a3a3a3]">{filtered.length} quote{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Snapshot preview */}
        {showSnapshot && (
          <div className="bg-[#1a1a2e] text-green-300 rounded-xl p-4 mb-4 font-mono text-xs overflow-auto max-h-64">
            <div className="text-[#a3a3a3] mb-2">// Resolved snapshot for pricing.quotes at as_of_date = "{effectiveSelectedDate}" ({resolutionMode} mode)</div>
            <pre>{JSON.stringify(snapshot, null, 2)}</pre>
          </div>
        )}

        {/* Calendar */}
        <Calendar
          month={calendarMonth}
          selectedDate={effectiveSelectedDate}
          onSelectDate={handleDateChange}
          onChangeMonth={handleMonthChange}
          overview={monthOverview}
          entries={entries}
          globalResolutionMode={resolutionMode}
          loadingOverview={hydrating}
        />

        {/* New quote form */}
        {showNewQuote && (
          <NewQuoteForm
            onSave={(entry) => { saveQuoteBookEntry(entry); reload(); setShowNewQuote(false); flash('success', 'Quote added'); }}
            onCancel={() => setShowNewQuote(false)}
            existingIds={entries.map(e => e.id)}
          />
        )}

        {/* Main layout — flex column filling remaining viewport, no sticky */}
        <div className="flex flex-col" style={{ height: 'calc(100vh - 6rem)' }}>
          {/* Graph panel — fixed slot at top, never scrolls */}
          <div className="shrink-0 mb-3">
            {selectedEntry ? (
              <QuoteSeriesPanel
                entry={selectedEntry}
                asOfDate={effectiveSelectedDate}
                resolutionMode={resolutionMode}
                onUpdate={() => reload()}
                formatValue={formatValue}
              />
            ) : (
              <PanelPlaceholder
                compact
                icon="timeSeries"
                title="No quote selected"
                description="Select a quote to view and edit its time series."
              />
            )}
            <div className="bg-white border border-[#e5e5e5] rounded-xl p-3 mt-2">
              <div className="flex flex-wrap items-center gap-3 text-xs text-[#737373]">
                <span>
                  Selected date: <span className="font-mono text-[#525252]">{effectiveSelectedDate}</span>
                </span>
                <span>Resolution: <span className="font-medium text-[#525252]">{resolutionMode}</span></span>
                {resolveStats && (
                  <>
                    <span>Resolved: <span className="font-medium text-[#525252]">{resolveStats.foundQuotes}</span></span>
                    <span>Exact: <span className="font-medium text-[#525252]">{resolveStats.exactQuotes}</span></span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Table — fills remaining space, scrolls internally */}
          <div className="flex-1 min-h-0 flex flex-col bg-white border border-[#e5e5e5] rounded-xl overflow-hidden">
            {filtered.length === 0 ? (
              <div className="p-6 flex-1">
                <PanelPlaceholder
                  icon={quoteUi.icon}
                  title="No quotes in the book"
                  description="Add quotes with time series to start building your market data history."
                  actionLabel="Add Quote"
                  onAction={() => { setShowNewQuote(true); setSelectedQuoteId(null); }}
                />
              </div>
            ) : (
              <>
                {/* Fixed header */}
                <div className="shrink-0 border-b border-[#e5e5e5] overflow-x-auto">
                  <table className="w-full min-w-[760px] table-fixed">
                    <colgroup>
                      <col className="w-[28%]" />
                      <col className="w-[18%]" />
                      <col className="w-[12%]" />
                      <col className="w-[14%]" />
                      <col className="w-[14%]" />
                      <col className="w-[14%]" />
                    </colgroup>
                    <thead>
                      <tr className="bg-[#fafafa]">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider whitespace-nowrap">ID</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider whitespace-nowrap">Label</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider whitespace-nowrap">Type</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider whitespace-nowrap">Quote Type</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider whitespace-nowrap">Value @ {effectiveSelectedDate}</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider whitespace-nowrap">Resolved</th>
                      </tr>
                    </thead>
                  </table>
                </div>
                {/* Scrollable body */}
                <div className="flex-1 min-h-0 overflow-auto">
                  <table className="w-full min-w-[760px] table-fixed">
                    <colgroup>
                      <col className="w-[28%]" />
                      <col className="w-[18%]" />
                      <col className="w-[12%]" />
                      <col className="w-[14%]" />
                      <col className="w-[14%]" />
                      <col className="w-[14%]" />
                    </colgroup>
                    <tbody>
                      {filtered.map(entry => {
                        const mode = getEffectiveResolutionMode(entry);
                        const resolved = resolveQuoteValue(entry.series, effectiveSelectedDate, mode);
                        const isSelected = selectedQuoteId === entry.id;
                        const exactValue = entry.series.find((p) => p.date === effectiveSelectedDate)?.value;
                        return (
                          <tr key={entry.id}
                            className={`border-b border-[#f5f5f5] cursor-pointer group transition-colors ${isSelected ? 'bg-[#f7f4ed]' : 'hover:bg-[#fafafa]'}`}
                            onClick={() => setSelectedQuoteId(isSelected ? null : entry.id)}>
                            <td className="px-4 py-3">
                              <code className="text-sm font-mono text-[#0a0a0a] block max-w-[20rem] truncate" title={entry.id}>{entry.id}</code>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#525252] max-w-[16rem] truncate" title={entry.label || '—'}>{entry.label || '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                                entry.kind === 'Rate' ? 'bg-blue-50 text-blue-700' :
                                entry.kind === 'Spread' ? 'bg-amber-50 text-amber-700' :
                                entry.kind === 'Price' ? 'bg-green-50 text-green-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{entry.kind}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#525252]">{effectiveQuoteType(entry)}</td>
                            <td className="px-4 py-3 text-right">
                              {exactValue !== undefined ? (
                                <span className={`font-mono text-sm ${isSelected ? 'text-[#111111] font-semibold' : 'text-[#0a0a0a]'}`}>
                                  {formatValue(entry, exactValue)}
                                </span>
                              ) : (
                                <span className={`text-xs ${isSelected ? 'text-[#4b5563] font-medium' : 'text-[#a3a3a3]'}`}>—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {resolved !== null ? (
                                <span className={`font-mono text-sm ${isSelected ? 'text-[#111111] font-semibold' : 'font-medium text-[#0a0a0a]'}`}>
                                  {formatValue(entry, resolved)}
                                </span>
                              ) : (
                                <span className={`text-xs font-medium ${isSelected ? 'text-red-700' : 'text-red-400'}`}>missing</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// ============================================================================
// Calendar — date completeness heatmap
// ============================================================================
function Calendar({
  month,
  selectedDate,
  onSelectDate,
  onChangeMonth,
  overview,
  entries,
  globalResolutionMode,
  loadingOverview,
}: {
  month: { year: number; month: number };
  selectedDate: string;
  onSelectDate: (date: string) => void;
  onChangeMonth: (next: { year: number; month: number }) => void;
  overview: MdMonthOverview | null;
  entries: QuoteBookEntry[];
  globalResolutionMode: QuoteResolutionMode;
  loadingOverview: boolean;
}) {
  const { year, month: monthIndex } = month;
  const first = new Date(year, monthIndex, 1);
  const last = new Date(year, monthIndex + 1, 0);
  const startDay = first.getDay(); // 0-6
  const daysInMonth = last.getDate();
  const weeks: Array<Array<number | null>> = [];
  let currentDay = 1;
  for (let w = 0; w < 6; w++) {
    const week: Array<number | null> = [];
    for (let d = 0; d < 7; d++) {
      if (w === 0 && d < startDay) {
        week.push(null);
      } else if (currentDay > daysInMonth) {
        week.push(null);
      } else {
        week.push(currentDay++);
      }
    }
    weeks.push(week);
  }

  const formatDate = (day: number) => {
    const mm = String(monthIndex + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  };

  const dayStats: Record<string, { exact: number; resolved: number }> = {};
  const totalQuotes = overview?.total_series || entries.length;
  const byDate = new Map((overview?.days || []).map((d) => [d.date, d]));
  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = formatDate(day);
    const row = byDate.get(dateKey);
    if (row) {
      dayStats[dateKey] = { exact: row.exact_count || 0, resolved: row.resolved_count || 0 };
      continue;
    }

    // Fallback to local coverage when backend overview is unavailable.
    let exact = 0;
    let resolved = 0;
    for (const entry of entries) {
      if (entry.series.some((p) => p.date === dateKey)) exact += 1;
      const mode = entry.resolution_mode || globalResolutionMode;
      if (resolveQuoteValue(entry.series, dateKey, mode) !== null) resolved += 1;
    }
    dayStats[dateKey] = { exact, resolved };
  }

  const getDayClass = (day: number) => {
    if (totalQuotes === 0) return 'bg-[#f5f5f5] text-[#a3a3a3] border-[#e5e5e5]';
    const dateKey = formatDate(day);
    const exactCount = dayStats[dateKey]?.exact || 0;
    const exactRatio = totalQuotes > 0 ? exactCount / totalQuotes : 0;
    if (exactRatio === 0) return 'bg-red-50 text-red-700 border-red-200';
    if (exactRatio === 1) return 'bg-green-50 text-green-700 border-green-200';
    return 'bg-amber-50 text-amber-800 border-amber-200';
  };

  const monthLabel = first.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-white border border-[#e5e5e5] rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-[#0a0a0a]">Calendar</div>
        <div className="flex items-center gap-2 min-w-[260px] justify-end">
          <button
            onClick={() => {
              const prev = new Date(year, monthIndex - 1, 1);
              onChangeMonth({ year: prev.getFullYear(), month: prev.getMonth() });
            }}
            disabled={loadingOverview}
            className={`w-14 px-2 py-1 text-xs font-medium border rounded-lg ${
              loadingOverview
                ? 'text-[#a3a3a3] bg-[#f5f5f5] border-[#e5e5e5] cursor-not-allowed'
                : 'text-[#525252] bg-white border-[#d4d4d4] hover:bg-[#f5f5f5]'
            }`}
          >
            Prev
          </button>
          <span className="text-xs text-[#525252] text-center w-36 tabular-nums inline-flex items-center justify-center gap-1.5">
            {loadingOverview && (
              <svg className="w-3.5 h-3.5 animate-spin text-[#737373]" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
            )}
            {monthLabel}
          </span>
          <button
            onClick={() => {
              const next = new Date(year, monthIndex + 1, 1);
              onChangeMonth({ year: next.getFullYear(), month: next.getMonth() });
            }}
            disabled={loadingOverview}
            className={`w-14 px-2 py-1 text-xs font-medium border rounded-lg ${
              loadingOverview
                ? 'text-[#a3a3a3] bg-[#f5f5f5] border-[#e5e5e5] cursor-not-allowed'
                : 'text-[#525252] bg-white border-[#d4d4d4] hover:bg-[#f5f5f5]'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-[10px] text-[#737373] mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="text-center">{d}</div>
        ))}
      </div>
      <div className="relative">
        <div className="grid grid-cols-7 gap-1">
          {weeks.flat().map((day, idx) => {
            if (!day) return <div key={idx} className="h-8" />;
            const dateKey = formatDate(day);
            const isSelected = dateKey === selectedDate;
            return (
              <button
                key={idx}
                onClick={() => onSelectDate(dateKey)}
                className={`h-8 rounded border text-xs font-semibold transition-colors ${getDayClass(day)} ${isSelected ? 'ring-2 ring-offset-1 ring-[#0a0a0a] border-[#0a0a0a] shadow-[0_0_0_2px_white] scale-[1.03]' : ''}`}
                title={`${dateKey} — exact ${dayStats[dateKey]?.exact || 0}/${totalQuotes || 0}, resolved ${dayStats[dateKey]?.resolved || 0}/${totalQuotes || 0}`}
              >
                {day}
              </button>
            );
          })}
        </div>
        {loadingOverview && (
          <div className="absolute inset-0 bg-white/65 backdrop-blur-[1px] rounded-md flex items-center justify-center">
            <div className="inline-flex items-center gap-2 text-xs text-[#525252] bg-white/90 border border-[#e5e5e5] rounded-full px-3 py-1">
              <svg className="w-3.5 h-3.5 animate-spin text-[#737373]" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
              Loading month...
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-[#737373]">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-50 border border-red-200"></span>No exact values</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-50 border border-amber-200"></span>Some exact values</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-50 border border-green-200"></span>All exact values</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded border-2 border-[#0a0a0a]"></span>Selected date</span>
      </div>
      <div className="mt-3 text-xs text-[#737373]">
        {loadingOverview ? 'Loading month coverage...' : <>Selected date: <span className="font-mono text-[#525252]">{selectedDate}</span></>}
      </div>
    </div>
  );
}

// ============================================================================
// New Quote Form
// ============================================================================
function NewQuoteForm({ onSave, onCancel, existingIds }: {
  onSave: (entry: QuoteBookEntry) => void;
  onCancel: () => void;
  existingIds: string[];
}) {
  const [id, setId] = useState('');
  const [kind, setKind] = useState<QuoteKind>('Rate');
  const [quoteType, setQuoteType] = useState<QuoteType>('Curve');
  const [label, setLabel] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    const qId = id.trim().toUpperCase().replace(/\s+/g, '_');
    if (!qId) { setError('ID is required'); return; }
    if (existingIds.includes(qId)) { setError('Quote ID already exists'); return; }
    onSave({ id: qId, kind, quote_type: quoteType, label: label.trim() || undefined, currency, series: [] });
  };

  const inputClass = formStyles.input;

  return (
    <div className="bg-white border border-[#e5e5e5] rounded-xl p-5 mb-4">
      <h3 className="text-sm font-semibold text-[#0a0a0a] mb-4">New Quote</h3>
      {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
      <div className="grid sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-[#737373] mb-1 font-medium">Quote ID *</label>
          <input type="text" value={id} onChange={e => { setId(e.target.value); setError(''); }}
            placeholder="e.g., EUR_OIS_5Y" className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-[#737373] mb-1 font-medium">Type</label>
          <select value={kind} onChange={e => setKind(e.target.value as QuoteKind)} className={inputClass}>
            {QUOTE_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#737373] mb-1 font-medium">Quote Type</label>
          <select value={quoteType} onChange={e => setQuoteType(e.target.value as QuoteType)} className={inputClass}>
            {QUOTE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#737373] mb-1 font-medium">Currency</label>
          <select value={currency} onChange={e => setCurrency(e.target.value)} className={inputClass}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#737373] mb-1 font-medium">Label</label>
          <input type="text" value={label} onChange={e => setLabel(e.target.value)}
            placeholder="Optional name" className={inputClass} />
        </div>
      </div>
      <div className="flex gap-2 mt-4 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-[#525252] bg-white border border-[#d4d4d4] rounded-lg hover:bg-[#f5f5f5]">Cancel</button>
        <button onClick={handleSubmit} className="px-4 py-2 text-sm font-medium text-white bg-[#0a0a0a] rounded-lg hover:bg-[#262626]">Create</button>
      </div>
    </div>
  );
}

// ============================================================================
// Quote Series Panel — chart + editor
// ============================================================================
function QuoteSeriesPanel({ entry, asOfDate, resolutionMode, onUpdate, formatValue }: {
  entry: QuoteBookEntry;
  asOfDate: string;
  resolutionMode: QuoteResolutionMode;
  onUpdate: () => void;
  formatValue: (entry: QuoteBookEntry, val: number) => string;
}) {
  const [selectedDateValue, setSelectedDateValue] = useState('');
  const isRate = entry.kind === 'Rate' || entry.kind === 'Spread';
  const effectiveMode: QuoteResolutionMode = entry.resolution_mode || resolutionMode;

  const resolved = resolveQuoteValue(entry.series, asOfDate, effectiveMode);
  const sortedSeries = [...entry.series].sort((a, b) => a.date.localeCompare(b.date));
  const exactPoint = sortedSeries.find((p) => p.date === asOfDate);

  useEffect(() => {
    if (!exactPoint) {
      setSelectedDateValue('');
      return;
    }
    const display = isRate ? (exactPoint.value * 100).toFixed(4) : String(exactPoint.value);
    setSelectedDateValue(display);
  }, [entry.id, asOfDate, exactPoint?.value, isRate]);

  const handleDeleteSelectedDate = () => {
    deleteDatedValue(entry.id, asOfDate);
    onUpdate();
  };

  const handleSaveSelectedDate = () => {
    const trimmed = selectedDateValue.trim();
    if (!trimmed) return;
    const numVal = parseFloat(trimmed);
    if (isNaN(numVal)) return;
    const actual = isRate ? numVal / 100 : numVal;
    upsertDatedValue(entry.id, asOfDate, actual);
    onUpdate();
  };

  const renderChart = () => {
    if (sortedSeries.length < 2) {
      return <div className="text-xs text-[#a3a3a3]">Add at least two points to show a chart</div>;
    }
    const values = sortedSeries.map(s => s.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const points = sortedSeries.map((s, i) => {
      const x = (i / (sortedSeries.length - 1)) * 100;
      const y = 100 - ((s.value - min) / range) * 100;
      return `${x},${y}`;
    }).join(' ');
    return (
      <svg viewBox="0 0 100 100" className="w-full h-28">
        <polyline fill="none" stroke="#8a6a2f" strokeWidth="2" points={points} />
      </svg>
    );
  };

  return (
    <div className="bg-white border border-[#e5e5e5] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-[#fafafa] border-b border-[#e5e5e5]">
        <code className="text-sm font-mono font-semibold text-[#0a0a0a]">{entry.id}</code>
        {entry.label && <span className="text-xs text-[#737373] ml-2">{entry.label}</span>}
        <div className="mt-1 flex items-center gap-2">
          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
            entry.kind === 'Rate' ? 'bg-blue-50 text-blue-700' :
            entry.kind === 'Spread' ? 'bg-amber-50 text-amber-700' :
            'bg-gray-100 text-gray-600'
          }`}>{entry.kind}</span>
          {entry.currency && <span className="text-xs text-[#a3a3a3]">{entry.currency}</span>}
          <span className="text-xs text-[#a3a3a3]">{entry.series.length} points</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-0 border-t border-[#e5e5e5]">
        {/* Chart left */}
        <div className="lg:col-span-2 px-4 py-3 border-b lg:border-b-0 lg:border-r border-[#e5e5e5]">
          <div className="text-xs text-[#737373] mb-2">History</div>
          <div className="h-24">
            {renderChart()}
          </div>
          <div className="mt-2 text-xs text-[#737373]">
            {sortedSeries.length > 0 ? (
              <>Range: {sortedSeries[0].date} to {sortedSeries[sortedSeries.length - 1].date}</>
            ) : (
              <>No points</>
            )}
          </div>
        </div>

        {/* Forms right */}
        <div className="px-4 py-3 space-y-3">
          <div className="p-2 rounded bg-[#f9f8f5] border border-[#ece7dc]">
            <div className="text-xs text-[#737373] mb-1">Resolved @ {asOfDate} ({effectiveMode})</div>
            {resolved !== null ? (
              <span className="font-mono text-base font-semibold text-[#0a0a0a]">{formatValue(entry, resolved)}</span>
            ) : (
              <span className="text-sm text-red-400 font-medium">No value available</span>
            )}
          </div>

          <div>
            <label className="block text-xs text-[#737373] mb-1">Resolution</label>
            <select
              value={entry.resolution_mode || 'global'}
              onChange={(e) => {
                const next = e.target.value;
                saveQuoteBookEntry({
                  ...entry,
                  resolution_mode: next === 'global' ? undefined : (next as QuoteResolutionMode),
                });
                onUpdate();
              }}
              className="w-full px-2 py-1.5 text-xs border border-[#d4d4d4] rounded-lg bg-white"
            >
              <option value="global">Use global ({resolutionMode})</option>
              <option value="previous">Previous (&lt;= date)</option>
              <option value="exact">Exact (empty if missing)</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <label className="block text-xs text-[#737373] mb-1">Selected Date Value{isRate ? ' (%)' : ''}</label>
              <input
                type="number"
                step="any"
                value={selectedDateValue}
                onChange={(e) => setSelectedDateValue(e.target.value)}
                placeholder={exactPoint ? undefined : 'No exact point'}
                className="w-full px-2 py-1.5 text-xs border border-[#d4d4d4] rounded-lg focus:outline-none focus:border-[#8a6a2f]"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSaveSelectedDate}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-[#0a0a0a] rounded-lg hover:bg-[#262626]"
              >
                Save
              </button>
              <button
                onClick={handleDeleteSelectedDate}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-[#525252] bg-white border border-[#d4d4d4] rounded-lg hover:bg-[#f5f5f5]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
